import type { PendingLogin, StoredTokens } from './types';

/**
 * Persistence boundary for tokens and the in-flight PKCE state.
 *
 * Web typically backs this with sessionStorage/localStorage; mobile apps
 * should back it with Expo SecureStore (or Keychain/Keystore directly).
 *
 * Implementations may be sync or async — all methods return promises.
 */
export interface TokenStore {
  getTokens(): Promise<StoredTokens | null>;
  setTokens(tokens: StoredTokens): Promise<void>;
  clearTokens(): Promise<void>;

  getPendingLogin(): Promise<PendingLogin | null>;
  setPendingLogin(pending: PendingLogin): Promise<void>;
  clearPendingLogin(): Promise<void>;
}

/** Volatile in-process store. Useful for tests and SSR. */
export class InMemoryTokenStore implements TokenStore {
  private tokens: StoredTokens | null = null;
  private pending: PendingLogin | null = null;

  async getTokens() {
    return this.tokens;
  }
  async setTokens(tokens: StoredTokens) {
    this.tokens = tokens;
  }
  async clearTokens() {
    this.tokens = null;
  }
  async getPendingLogin() {
    return this.pending;
  }
  async setPendingLogin(pending: PendingLogin) {
    this.pending = pending;
  }
  async clearPendingLogin() {
    this.pending = null;
  }
}

/**
 * Web Storage-backed store. Use sessionStorage for "log out on tab close"
 * semantics, localStorage to persist across tabs/sessions.
 */
export class WebStorageTokenStore implements TokenStore {
  private static readonly TOKENS_KEY = '@mrrmlab/auth:tokens';
  private static readonly PENDING_KEY = '@mrrmlab/auth:pending-login';

  constructor(private readonly storage: Storage) {}

  async getTokens() {
    return readJson<StoredTokens>(this.storage, WebStorageTokenStore.TOKENS_KEY);
  }
  async setTokens(tokens: StoredTokens) {
    this.storage.setItem(WebStorageTokenStore.TOKENS_KEY, JSON.stringify(tokens));
  }
  async clearTokens() {
    this.storage.removeItem(WebStorageTokenStore.TOKENS_KEY);
  }
  async getPendingLogin() {
    return readJson<PendingLogin>(this.storage, WebStorageTokenStore.PENDING_KEY);
  }
  async setPendingLogin(pending: PendingLogin) {
    this.storage.setItem(WebStorageTokenStore.PENDING_KEY, JSON.stringify(pending));
  }
  async clearPendingLogin() {
    this.storage.removeItem(WebStorageTokenStore.PENDING_KEY);
  }
}

function readJson<T>(storage: Storage, key: string): T | null {
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
